import {type TraceCallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/traceServerClientTypes';
import {
  useCalls,
  useRootObjectVersions,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/tsDataModelHooks';
import {type ObjectVersionFilter} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/wfDataModelHooksInterface';
import {parseRefMaybe} from '@wandb/weave/react';
import {useMemo} from 'react';

import {WEAVE_LATENCY_PATH, WEAVE_STATUS_PATH} from './constants';
import {type SourceType} from './types';
import {
  buildMonitorInputRefs,
  extractCostMetricKeys,
  getFlattenedKeysFromData,
} from './utils';

/**
 * Extracts metric keys from a call's traceCall data.
 * For scorer calls, we focus on the output since that's where scores are stored.
 */
const extractMetricKeys = (
  traceCall: TraceCallSchema | undefined
): string[] => {
  if (!traceCall) {
    return [];
  }

  const keys = new Set<string>([WEAVE_LATENCY_PATH, WEAVE_STATUS_PATH]);

  for (const field of ['inputs', 'output', 'attributes'] as const) {
    if (traceCall[field]) {
      for (const k of getFlattenedKeysFromData(traceCall[field], field)) {
        keys.add(k);
      }
    }
  }

  // Extract cost and token usage metrics from summary.weave.costs
  const costs = traceCall.summary?.weave?.costs;
  if (costs) {
    for (const k of extractCostMetricKeys(costs)) {
      keys.add(k);
    }
  }

  return [...keys].sort();
};

export const useGetMetricFromOp = (
  entityName: string,
  projectName: string,
  opStr: string
) => {
  const calls = useCalls({
    entity: entityName,
    project: projectName,
    filter: {opVersionRefs: [opStr]},
    limit: 1,
    sortBy: [{field: 'started_at', direction: 'desc'}],
    includeCosts: true,
  });

  const call = calls.result?.[0]?.traceCall;
  const allKeys = useMemo(() => extractMetricKeys(call), [call]);

  return {allKeys, call, loading: calls.loading};
};

/**
 * Hook to get metric paths from a monitor's scorer calls.
 *
 * Monitors work by executing scorer ops on target calls. The metric paths we want
 * to alert on (like output.score) come from the scorer call's output, not from
 * the monitored call's output.
 *
 * Flow:
 * 1. If a scorer is already selected, use it directly as an input_refs filter
 * 2. Otherwise, fetch the monitor object to get its `scorers` list (array of scorer object refs)
 * 3. Query for calls whose input_refs contain the scorer object ref
 * 4. Extract metric paths from the scorer call's output
 */
export const useGetMetricFromMonitor = (
  entityName: string,
  projectName: string,
  monitorRef: string,
  selectedScorerRef: string
) => {
  // If a specific scorer is already selected, query its calls directly
  // instead of re-fetching the monitor object to discover scorers.
  const hasSelectedScorer = Boolean(selectedScorerRef);

  const monitorId = useMemo(
    () =>
      monitorRef && !hasSelectedScorer
        ? (parseRefMaybe(monitorRef)?.artifactName ?? null)
        : null,
    [monitorRef, hasSelectedScorer]
  );

  // Fetch the monitor object to get its scorers (only when no scorer selected)
  const monitorFilter = useMemo(
    (): ObjectVersionFilter => ({
      objectIds: monitorId ? [monitorId] : [],
      baseObjectClasses: ['Monitor'],
      latestOnly: true,
    }),
    [monitorId]
  );

  const monitorResult = useRootObjectVersions({
    entity: entityName,
    project: projectName,
    filter: monitorFilter,
    metadataOnly: false,
  });

  // Extract scorer object refs from the monitor object
  const scorerRefs = useMemo(() => {
    if (!monitorResult.result?.[0]?.val?.scorers) {
      return [];
    }
    // Scorers are stored as object refs like weave:///entity/project/object/ScorerName:hash
    return monitorResult.result[0].val.scorers as string[];
  }, [monitorResult.result]);

  // Scorer refs are object refs (e.g. weave:///entity/project/object/check-judge:hash),
  // not op refs. The scorer's calls have the scorer object ref in their input_refs,
  // so we filter by inputObjectVersionRefs to find the correct calls.
  const inputObjectVersionRefs = useMemo(() => {
    let directRefs: string[];
    if (hasSelectedScorer) {
      directRefs = [selectedScorerRef];
    } else if (scorerRefs.length > 0) {
      directRefs = [...scorerRefs];
    } else {
      directRefs = [];
    }
    const refs = buildMonitorInputRefs(directRefs, monitorRef);
    return refs.length > 0 ? refs : undefined;
  }, [hasSelectedScorer, selectedScorerRef, scorerRefs, monitorRef]);

  const shouldSkip =
    !hasSelectedScorer && scorerRefs.length === 0 && !monitorRef;

  // Query for calls that reference the scorer object in their inputs
  const calls = useCalls({
    entity: entityName,
    project: projectName,
    filter: {inputObjectVersionRefs},
    limit: 1,
    sortBy: [{field: 'started_at', direction: 'desc'}],
    skip: shouldSkip,
    includeCosts: true,
  });

  const call = calls.result?.[0]?.traceCall;
  const allKeys = useMemo(() => extractMetricKeys(call), [call]);

  // Extract the scorer's op ref from the sample call (e.g.
  // "weave:///entity/project/op/LLMAsAJudgeScorer.score:hash").
  // Used as op_str in the alert trigger for efficient server-side filtering.
  const scorerOpRef = call?.op_name ?? undefined;

  const loading =
    (!hasSelectedScorer && monitorResult.loading) || calls.loading;

  return {allKeys, call, loading, scorerOpRef};
};

export const useGetMetricFromSource = (
  entityName: string,
  projectName: string,
  sourceType: SourceType,
  opStr: string,
  monitorRef: string
) => {
  const opResult = useGetMetricFromOp(
    entityName,
    projectName,
    sourceType === 'OPERATION' ? opStr : ''
  );

  const monitorResult = useGetMetricFromMonitor(
    entityName,
    projectName,
    sourceType === 'MONITOR' ? monitorRef : '',
    sourceType === 'MONITOR' ? opStr : ''
  );

  // For monitor alerts, expose the resolved scorer op ref (from the sample
  // call's opName) so it can be stored and sent as op_str for efficient
  // server-side filtering alongside scorer_ref.
  const scorerOpRef =
    sourceType === 'MONITOR' ? monitorResult.scorerOpRef : undefined;

  const base = sourceType === 'MONITOR' ? monitorResult : opResult;
  return {...base, scorerOpRef};
};
